import { styled, Typography } from '@ori-ui/mui';
import type { Language } from '@ori/language-switcher';
import { changeLanguage } from '@ori/language-switcher';
import React, { useCallback, useMemo } from 'react';

import { maxWidth, maxTextWidth } from '../../constants';
import { useAppConfig, useFooterContext } from '../../hooks';
import { MobileOfficeRoot } from '../MobileOffice';

const PREFIX = 'LanguageSelector';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  textBlock: `${PREFIX}-textBlock`,
  active: `${PREFIX}-active`,
  links: `${PREFIX}-links`,
};

const Root = styled(MobileOfficeRoot)(({ theme }) => ({
  [`& .${classes.root}`]: {
    maxWidth,
    margin: theme.spacing(0, 'auto', 6),
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 6),
    },
  },

  [`& .${classes.content}`]: {
    display: 'block',
    margin: '0 auto',
    maxWidth: maxTextWidth,
  },

  [`& .${classes.textBlock}`]: {
    display: 'inline-block',
    margin: theme.spacing(0, 2),
  },

  [`& .${classes.active}`]: {
    fontWeight: 'bold',
  },

  [`& .${classes.links}`]: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

const MISSING_LABEL = 'Missing label';

const renderCountryInfo = (rootClassName: unknown, countryLabel?: string | null, countryValue?: string | null) => {
  if (countryLabel === '' && countryValue === '') {
    return null;
  }

  return (
    <Typography
      variant="caption"
      className={rootClassName as string}
    >
      {countryLabel ?? MISSING_LABEL}: {countryValue ?? MISSING_LABEL}
    </Typography>
  );
};

const noop = () => {};

export const LanguageSelector: React.FC = () => {
  const { currentLanguage, tenant } = useAppConfig();
  const { data, isMobileOffice } = useFooterContext();

  const langConfig = data?.application?.footer?.languageConfiguration;
  const languages = useMemo(
    () =>
      (data?.languages ?? []).map((value) => ({
        code: value.code ?? '',
        name: value.name ?? '',
        type: value.type,
      })),
    [data?.languages],
  );
  const onChangeLanguage = useCallback(
    (nextLanguage: Language) => () => {
      changeLanguage(nextLanguage, currentLanguage, tenant);
    },
    [currentLanguage, tenant],
  );

  const isSelected = (languageId: string) => currentLanguage === languageId;
  const resolveActiveClass = (languageId: string) => (isSelected(languageId) ? classes.active : classes.links);

  // if no languages sent (or just one language), render only country
  if (!languages.length || languages.length === 1) {
    const countryInfo = renderCountryInfo(classes.textBlock, langConfig?.countryLabel, langConfig?.currentCountry);

    if (!countryInfo) {
      return null;
    }

    return (
      <Root isMobileOffice={isMobileOffice}>
        <div className={classes.content}>{countryInfo}</div>
      </Root>
    );
  }

  return (
    <Root isMobileOffice={isMobileOffice}>
      <div className={classes.content}>
        <Typography
          variant="caption"
          className={classes.textBlock}
        >
          {langConfig?.languageLabel ?? MISSING_LABEL}:{' '}
          {languages.map((lang, index) => (
            <React.Fragment key={lang.code || index}>
              <span
                className={resolveActiveClass(lang.code)}
                role="button"
                tabIndex={0}
                onClick={isSelected(lang.code) ? noop : onChangeLanguage(lang)}
                onKeyDown={isSelected(lang.code) ? noop : onChangeLanguage(lang)}
              >
                {lang.name}
              </span>
              {languages.length - 1 === index ? null : ' / '}
            </React.Fragment>
          ))}
        </Typography>
        {renderCountryInfo(classes.textBlock, langConfig?.countryLabel, langConfig?.currentCountry)}
      </div>
    </Root>
  );
};
